// This file contains functions that should be added to the oiltanksweep (main app)
// to handle synchronization with the oiltankportal app

import { doc, getDoc, updateDoc, collection, query, where, getDocs, deleteDoc, arrayUnion } from 'firebase/firestore';

// Import your Firestore database references
// In oiltanksweep app, you'd use your own references:
// const db = firebase.firestore();
// const portalDb = firebase.firestore(portalApp);

/**
 * Function to notify portal clients that a report is no longer available
 * 
 * @param {string} reportId - The ID of the report being removed
 * @param {object} db - The main app Firestore database reference
 * @param {object} portalDb - The portal app Firestore database reference
 * @returns {Promise<object>} - Result of the operation
 */
export const notifyReportRemoved = async (reportId, db, portalDb) => {
  try {
    // 1. Update the report in the main database to mark it as removed
    const reportRef = doc(db, 'inspections', reportId);
    const reportDoc = await getDoc(reportRef);
    
    if (!reportDoc.exists()) {
      console.error(`Report ${reportId} not found`);
      return { success: false, error: 'Report not found' };
    }
    
    const reportData = reportDoc.data();
    
    // Update the report with removed status
    await updateDoc(reportRef, {
      status: 'removed',
      removedAt: new Date().toISOString(),
      // You might want to keep the original status for restoration
      previousStatus: reportData.status || 'active',
      // Any other fields you need to update
    });
    
    // 2. Find all portal users who have this report saved
    const usersWithReportQuery = query(
      collection(portalDb, 'users'),
      where('savedReports', 'array-contains', reportId)
    );
    
    const usersWithReport = await getDocs(usersWithReportQuery);
    
    if (usersWithReport.empty) {
      console.log(`No portal users have report ${reportId} saved`);
      return { 
        success: true, 
        message: 'Report removed. No portal users affected.' 
      };
    }
    
    // 3. Add notifications to each user
    const notificationPromises = usersWithReport.docs.map(async (userDoc) => {
      const userData = userDoc.data();
      const notifications = userData.notifications || [];
      
      // Create new notification
      const newNotification = {
        type: 'report_removed',
        title: 'Report No Longer Available',
        message: `The report for "${reportData.propertyAddress || 'Unknown Property'}" has been removed and is no longer available.`,
        reportId: reportId,
        propertyAddress: reportData.propertyAddress || 'Unknown Property',
        clientName: reportData.clientName || 'Unknown Client',
        timestamp: new Date().toISOString(),
        read: false,
        id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };
      
      // Add to beginning of array so newest are first
      const updatedNotifications = [newNotification, ...notifications];
      
      // Update user document
      return updateDoc(doc(portalDb, 'users', userDoc.id), {
        notifications: updatedNotifications
      });
    });
    
    await Promise.all(notificationPromises);
    
    console.log(`Report ${reportId} removed and ${usersWithReport.size} portal users notified`);
    
    return { 
      success: true, 
      usersNotified: usersWithReport.size,
      message: `Report removed and ${usersWithReport.size} portal users notified`
    };
    
  } catch (error) {
    console.error('Error removing report:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Function to restore a report that was previously removed
 * 
 * @param {string} reportId - The ID of the report to restore
 * @param {object} db - The main app Firestore database reference
 * @param {object} portalDb - The portal app Firestore database reference
 * @param {boolean} notifyUsers - Whether to notify users about the restoration
 * @returns {Promise<object>} - Result of the operation
 */
export const restoreReportFromRemoved = async (reportId, db, portalDb, notifyUsers = false) => {
  try {
    // 1. Update the report in the main database to restore it
    const reportRef = doc(db, 'inspections', reportId);
    const reportDoc = await getDoc(reportRef);
    
    if (!reportDoc.exists()) {
      console.error(`Report ${reportId} not found`);
      return { success: false, error: 'Report not found' };
    }
    
    const reportData = reportDoc.data();
    
    // Restore the previous status or set as active
    await updateDoc(reportRef, {
      status: reportData.previousStatus || 'active',
      removedAt: null,
      previousStatus: null,
      restoredAt: new Date().toISOString()
    });
    
    // If we don't need to notify users, we're done
    if (!notifyUsers) {
      return { 
        success: true, 
        message: 'Report restored from removed. No notifications sent.' 
      };
    }
    
    // 2. Find all portal users who have this report saved
    const usersWithReportQuery = query(
      collection(portalDb, 'users'),
      where('savedReports', 'array-contains', reportId)
    );
    
    const usersWithReport = await getDocs(usersWithReportQuery);
    
    if (usersWithReport.empty) {
      return { 
        success: true, 
        message: 'Report restored from removed. No portal users affected.' 
      };
    }
    
    // 3. Add notifications about restoration
    const notificationPromises = usersWithReport.docs.map(async (userDoc) => {
      const userData = userDoc.data();
      const notifications = userData.notifications || [];
      
      // Create new notification
      const newNotification = {
        type: 'report_restored',
        title: 'Report Restored',
        message: `The report for "${reportData.propertyAddress || 'Unknown Property'}" has been restored and is now available again.`,
        reportId: reportId,
        propertyAddress: reportData.propertyAddress || 'Unknown Property',
        clientName: reportData.clientName || 'Unknown Client',
        timestamp: new Date().toISOString(),
        read: false,
        id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };
      
      // Add to beginning of array so newest are first
      const updatedNotifications = [newNotification, ...notifications];
      
      // Update user document
      return updateDoc(doc(portalDb, 'users', userDoc.id), {
        notifications: updatedNotifications
      });
    });
    
    await Promise.all(notificationPromises);
    
    return { 
      success: true, 
      usersNotified: usersWithReport.size,
      message: `Report restored from removed and ${usersWithReport.size} portal users notified`
    };
    
  } catch (error) {
    console.error('Error restoring report from removed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Function to permanently delete a report from the main app
 * This will completely remove the report and notify portal users
 * 
 * @param {string} reportId - The ID of the report being deleted
 * @param {object} db - The main app Firestore database reference
 * @param {object} portalDb - The portal app Firestore database reference
 * @returns {Promise<object>} - Result of the operation
 */
export const permanentlyDeleteReport = async (reportId, db, portalDb) => {
  try {
    // 1. Get report data before deletion for notifications
    const reportRef = doc(db, 'inspections', reportId);
    const reportDoc = await getDoc(reportRef);
    
    if (!reportDoc.exists()) {
      console.error(`Report ${reportId} not found`);
      return { success: false, error: 'Report not found' };
    }
    
    const reportData = reportDoc.data();
    
    // 2. Find all portal users who have this report saved
    const usersWithReportQuery = query(
      collection(portalDb, 'users'),
      where('savedReports', 'array-contains', reportId)
    );
    
    const usersWithReport = await getDocs(usersWithReportQuery);
    let usersCount = 0;
    
    if (!usersWithReport.empty) {
      usersCount = usersWithReport.size;
      
      // 3. Add notifications about permanent deletion
      const notificationPromises = usersWithReport.docs.map(async (userDoc) => {
        const userData = userDoc.data();
        const notifications = userData.notifications || [];
        
        // Create new notification
        const newNotification = {
          type: 'report_deleted',
          title: 'Report Permanently Deleted',
          message: `The report for "${reportData.propertyAddress || 'Unknown Property'}" has been permanently deleted.`,
          reportId: reportId,
          propertyAddress: reportData.propertyAddress || 'Unknown Property',
          clientName: reportData.clientName || 'Unknown Client',
          timestamp: new Date().toISOString(),
          read: false,
          id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
        
        // Add to beginning of array so newest are first
        const updatedNotifications = [newNotification, ...notifications];
        
        // Update user document
        return updateDoc(doc(portalDb, 'users', userDoc.id), {
          notifications: updatedNotifications,
          // Optionally remove the report from savedReports
          savedReports: userData.savedReports.filter(id => id !== reportId)
        });
      });
      
      await Promise.all(notificationPromises);
    }
    
    // 4. Delete the actual report from the database
    await deleteDoc(reportRef);
    
    return { 
      success: true, 
      usersNotified: usersCount,
      message: `Report permanently deleted and ${usersCount} portal users affected`
    };
    
  } catch (error) {
    console.error('Error permanently deleting report:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Function to notify portal clients when a report is shared with them
 * 
 * @param {string} reportId - The ID of the report being shared
 * @param {string} clientEmail - The email of the client to share with
 * @param {object} db - The main app Firestore database reference
 * @param {object} portalDb - The portal app Firestore database reference
 * @returns {Promise<object>} - Result of the operation
 */
export const notifyReportShared = async (reportId, clientEmail, db, portalDb) => {
  try {
    console.log(`==== NOTIFICATION SYSTEM ====`);
    console.log(`Starting notifyReportShared process for report ${reportId} and client ${clientEmail}`);
    
    // 1. Get the report data
    const reportRef = doc(db, 'inspections', reportId);
    const reportDoc = await getDoc(reportRef);
    
    if (!reportDoc.exists()) {
      console.error(`Report ${reportId} not found`);
      return { success: false, error: 'Report not found' };
    }
    
    const reportData = reportDoc.data();
    console.log(`Found report data for ${reportId}: "${reportData.propertyAddress}"`);
    
    // 2. Find the portal user with the specified email
    const portalUsersQuery = query(
      collection(portalDb, 'users'),
      where('email', '==', clientEmail)
    );
    
    const portalUsers = await getDocs(portalUsersQuery);
    
    if (portalUsers.empty) {
      console.log(`No portal user found with email: ${clientEmail}`);
      return { success: false, error: 'User not found in portal' };
    }
    
    console.log(`Found ${portalUsers.size} portal users with email ${clientEmail}`);
    
    // Track what happened during processing
    const processResults = {
      newReportNotifications: 0,
      updateNotifications: 0
    };
    
    // Initialize or get the sharing record for this report
    let sharingRecord = reportData.sharingRecord || {};
    const timestamp = new Date().toISOString();
    
    // 3. Process each matching user
    for (const userDoc of portalUsers.docs) {
      const userData = userDoc.data();
      const userId = userDoc.id;
      
      // Check if we've ever shared this report with this user before by looking at our sharing record
      const isFirstShare = !sharingRecord[clientEmail];
      
      console.log(`User ID: ${userId}, Email: ${clientEmail}`);
      console.log(`Is this the first time sharing this report with this user? ${isFirstShare ? 'YES' : 'NO'}`);
      
      // Get existing notifications
      const existingNotifications = userData.notifications || [];
      const savedReports = userData.savedReports || [];
      
      if (isFirstShare) {
        // CASE 1: This is the FIRST time we're sharing this report with this user
        console.log(`✨ NEW REPORT SCENARIO: First time sharing report ${reportId} with ${clientEmail}`);
        
        // Create a NEW REPORT notification
        const newReportNotification = {
          type: 'report_added',
          title: 'New Report Available',
          message: `A new report for "${reportData.propertyAddress || 'Unknown Property'}" is now available.`,
          reportId: reportId,
          propertyAddress: reportData.propertyAddress || 'Unknown Property',
          clientName: reportData.clientName || 'Unknown Client',
          timestamp: timestamp,
          read: false,
          id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
        
        console.log(`Created NEW REPORT notification: "${newReportNotification.title}"`);
        
        // IMPORTANT: Add the notification BEFORE adding the report to savedReports
        await updateDoc(doc(portalDb, 'users', userId), {
          notifications: [newReportNotification, ...existingNotifications]
        });
        console.log(`Added NEW REPORT notification to user's notifications`);
        processResults.newReportNotifications++;
        
        // Add report to savedReports if not already there
        if (!savedReports.some(id => String(id) === String(reportId))) {
          await updateDoc(doc(portalDb, 'users', userId), {
            savedReports: [...savedReports, reportId]
          });
          console.log(`Updated savedReports array for user ${userId}, added report ${reportId}`);
        }
        
        // Record this first share in the report's sharing record
        sharingRecord[clientEmail] = {
          firstSharedAt: timestamp,
          lastSharedAt: timestamp
        };
      } else {
        // CASE 2: We've shared this report with this user before - it's an UPDATE
        console.log(`🔄 UPDATE SCENARIO: Report ${reportId} was previously shared with ${clientEmail}`);
        
        // Check if we're within the cooldown period
        const lastSharedAt = sharingRecord[clientEmail].lastSharedAt;
        const lastSharedTime = new Date(lastSharedAt).getTime();
        const currentTime = new Date(timestamp).getTime();
        
        // Default cooldown: 15 minutes (in milliseconds)
        // You can adjust this as needed - set to 0 to disable cooldown
        const NOTIFICATION_COOLDOWN_MS = 15 * 60 * 1000; 
        
        const timeSinceLastShare = currentTime - lastSharedTime;
        const isWithinCooldown = timeSinceLastShare < NOTIFICATION_COOLDOWN_MS;
        
        console.log(`Last shared: ${new Date(lastSharedTime).toLocaleString()}`);
        console.log(`Current time: ${new Date(currentTime).toLocaleString()}`);
        console.log(`Time since last share: ${Math.round(timeSinceLastShare / 1000 / 60)} minutes`);
        console.log(`Is within cooldown period? ${isWithinCooldown ? 'YES' : 'NO'}`);
        
        // Only send update notification if outside cooldown period
        if (!isWithinCooldown) {
          // Create an UPDATE notification
          const updateNotification = {
            type: 'report_updated',
            title: 'Report Updated',
            message: `The report for "${reportData.propertyAddress || 'Unknown Property'}" has been updated.`,
            reportId: reportId,
            propertyAddress: reportData.propertyAddress || 'Unknown Property',
            clientName: reportData.clientName || 'Unknown Client',
            timestamp: timestamp,
            read: false,
            id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
          };
          
          console.log(`Created UPDATE notification: "${updateNotification.title}"`);
          
          // Add the update notification
          await updateDoc(doc(portalDb, 'users', userId), {
            notifications: [updateNotification, ...existingNotifications]
          });
          console.log(`Added UPDATE notification to user's notifications`);
          processResults.updateNotifications++;
        } else {
          console.log(`Skipping update notification - within cooldown period (${NOTIFICATION_COOLDOWN_MS/60000} minutes)`);
        }
        
        // Make sure the report is in the user's savedReports
        if (!savedReports.some(id => String(id) === String(reportId))) {
          await updateDoc(doc(portalDb, 'users', userId), {
            savedReports: [...savedReports, reportId]
          });
          console.log(`Report wasn't in savedReports (unusual for update), added it now`);
        }
        
        // Update the lastSharedAt timestamp
        sharingRecord[clientEmail].lastSharedAt = timestamp;
      }
    }
    
    // 4. Update the report's sharing record
    await updateDoc(reportRef, {
      sharingRecord: sharingRecord
    });
    console.log(`Updated report sharing record for ${reportId}`);
    
    console.log(`Successfully completed all notification operations for report ${reportId}`);
    console.log(`Results: ${processResults.newReportNotifications} new report notifications, ${processResults.updateNotifications} update notifications`);
    console.log(`==== END NOTIFICATION SYSTEM ====`);
    
    return { 
      success: true, 
      results: processResults,
      message: `Report shared with client ${clientEmail} and appropriate notifications sent`
    };
  } catch (error) {
    console.error('Error sharing report with client:', error);
    return { success: false, error: error.message };
  }
};

/*
 * Example implementation for the oiltanksweep app:
 * 
 * This code shows how to integrate the report sharing notification
 * into the main oiltanksweep app's report sharing functionality.
 */

/*
// In the oiltanksweep app, add this to your report sharing code:
import { notifyReportShared } from './portalSync';
import { db } from './firebase'; // Your main app database
import { portalDb } from './portalFirebase'; // Your portal app database connection

// Inside your report sharing function
const shareReportWithClient = async (reportId, clientEmail) => {
  try {
    // Your existing code to share the report...
    
    // Then add this to notify the client in the portal
    const notificationResult = await notifyReportShared(
      reportId, 
      clientEmail,
      db,  // Main app database reference
      portalDb  // Portal app database reference
    );
    
    if (notificationResult.success) {
      console.log('Client notified successfully');
      // Maybe show a success toast
    } else {
      console.error('Error notifying client:', notificationResult.error);
      // Maybe show an error toast but still continue
    }
    
    // Rest of your sharing code...
  } catch (error) {
    console.error('Error sharing report:', error);
    // Handle error
  }
};
*/ 